import { renderLucideIcon } from '../utils/lucide-icons.js'

function normalizeEmailPreviewUi(result) {
  if (!result || typeof result !== 'object') return null

  var ui = result.ui || result.emailPreviewUi || null
  if (!ui || typeof ui !== 'object') return null
  if (String(ui.type || '').toLowerCase() !== 'email-preview') return null

  var emails = Array.isArray(ui.emails)
    ? ui.emails.map(normalizeEmailPreviewEmail).filter(Boolean)
    : []

  return {
    type: 'email-preview',
    batchId: String(ui.batchId || result.batchId || '').trim(),
    targetLanguage: String(ui.targetLanguage || '').trim(),
    targetLanguageLabel: String(ui.targetLanguageLabel || 'Langue cible'),
    targetLanguageDisplay: String(ui.targetLanguageDisplay || '').trim(),
    title: String(ui.title || 'Prévisualisation des emails'),
    countLabel: String(ui.countLabel || 'Emails du lot'),
    validatedCountLabel: String(ui.validatedCountLabel || 'Emails validés'),
    sendButtonTemplate: String(ui.sendButtonTemplate || 'Envoyer les {{count}} emails validés'),
    recipientLabel: String(ui.recipientLabel || 'Destinataire'),
    subjectLabel: String(ui.subjectLabel || 'Objet'),
    bodyLabel: String(ui.bodyLabel || "Corps email"),
    customPromptReportLabel: String(ui.customPromptReportLabel || 'Choix de rédaction effectués'),
    customPromptAppliedLabel: String(ui.customPromptAppliedLabel || 'Applications'),
    customPromptLimitationsLabel: String(ui.customPromptLimitationsLabel || 'Limitations'),
    previousLabel: String(ui.previousLabel || 'Précédent'),
    nextLabel: String(ui.nextLabel || 'Suivant'),
    validateLabel: String(ui.validateLabel || 'Valider'),
    validatedLabel: String(ui.validatedLabel || 'Validé'),
    pageLabel: String(ui.pageLabel || 'Page'),
    activeEmailId: String(ui.activeEmailId || result.activeEmailId || '').trim(),
    emails: emails,
    validatedCount: normalizeCount(ui.validatedCount),
    totalCount: normalizeCount(ui.totalCount || emails.length),
    pageSize: normalizeCount(ui.pageSize || 10) || 10,
    testAction: normalizeCampaignTestAction(ui.testAction),
    nextStep: normalizeNextStep(ui.nextStep)
  }
}

function normalizeCampaignTestAction(action) {
  if (!action || typeof action !== 'object') return null
  var label = String(action.label || '').trim()
  var batchId = String(action.batchId || '').trim()
  if (!label || !batchId) return null
  return {
    action: String(action.action || 'campaign_test').trim() || 'campaign_test',
    label: label,
    batchId: batchId,
    enabled: action.enabled !== false
  }
}

function normalizeEmailPreviewEmail(email) {
  if (!email || typeof email !== 'object') return null
  var recipient = email.recipient && typeof email.recipient === 'object' ? email.recipient : {}
  var name = String(recipient.name || '').trim()
  var subject = String(email.subject || '').trim()
  var body = String(email.body || '')
  if (!name && !subject && !body) return null

  return {
    id: String(email.id || '').trim(),
    recipient: {
      name: name,
      jobTitle: String(recipient.jobTitle || '').trim(),
      company: String(recipient.company || '').trim(),
      email: String(recipient.email || '').trim(),
      contactId: String(recipient.contactId || '').trim(),
      status: String(recipient.status || '').trim(),
      statusLabel: String(recipient.statusLabel || '').trim(),
      sourceObject: normalizeSourceObject(recipient.sourceObject)
    },
    subject: subject,
    body: body,
    validated: email.validated === true,
    customPrompt: normalizeCustomPrompt(email.customPrompt),
    sendingProfile: normalizeWritingPolicy(email.sendingProfile)
  }
}

function normalizeCustomPrompt(customPrompt) {
  if (!customPrompt || typeof customPrompt !== 'object') return null
  var report = customPrompt.report && typeof customPrompt.report === 'object' ? customPrompt.report : customPrompt
  var status = String(report.status || '').trim()
  if (['applied', 'partially_applied', 'not_applied'].indexOf(status) === -1) return null

  return {
    report: {
      status: status,
      applied: normalizeStringList(report.applied),
      limitations: normalizeStringList(report.limitations)
    }
  }
}

function normalizeSourceObject(sourceObject) {
  if (!sourceObject || typeof sourceObject !== 'object') return null
  var label = String(sourceObject.label || '').trim()
  var type = String(sourceObject.type || '').trim()
  if (!label) return null
  return {
    type: type,
    typeLabel: String(sourceObject.typeLabel || type || 'Objet').trim(),
    id: String(sourceObject.id || '').trim(),
    label: label
  }
}

function normalizeWritingPolicy(policy) {
  if (!policy || typeof policy !== 'object') return null
  var policyName = String(policy.policyName || '').trim()
  var policyId = String(policy.policyId || '').trim()
  if (!policyName && !policyId) return null

  return {
    policyId: policyId,
    policyVersion: normalizeCount(policy.policyVersion) || 1,
    policyName: policyName || policyId,
    resolvedAt: String(policy.resolvedAt || '').trim(),
    resolverVersion: String(policy.resolverVersion || '').trim(),
    appliedRuleIds: normalizeStringList(policy.appliedRuleIds),
    dataFields: Array.isArray(policy.dataFields) ? policy.dataFields.map(normalizeDataField).filter(Boolean) : [],
    appliedLanguageRules: Array.isArray(policy.appliedLanguageRules) ? policy.appliedLanguageRules.map(normalizeRule).filter(Boolean) : [],
    appliedContentRules: Array.isArray(policy.appliedContentRules) ? policy.appliedContentRules.map(normalizeRule).filter(Boolean) : [],
    appliedDocumentRules: Array.isArray(policy.appliedDocumentRules) ? policy.appliedDocumentRules.map(normalizeDocumentRule).filter(Boolean) : [],
    selectedDocuments: Array.isArray(policy.selectedDocuments) ? policy.selectedDocuments.map(normalizePolicyDocument).filter(Boolean) : []
  }
}

function normalizeDataField(field) {
  if (!field || typeof field !== 'object') return null
  var scope = String(field.scope || '').trim()
  var path = String(field.path || '').trim()
  if (!scope || !path) return null
  return {
    scope: scope,
    path: path,
    label: String(field.label || '').trim(),
    required: field.required === true
  }
}

function normalizeRule(rule) {
  if (!rule || typeof rule !== 'object') return null
  var id = String(rule.id || '').trim()
  var label = String(rule.label || '').trim()
  if (!id && !label) return null
  return {
    id: id,
    label: label || id,
    priority: normalizeCount(rule.priority),
    language: String(rule.language || '').trim(),
    politeness: String(rule.politeness || '').trim(),
    instructions: normalizeStringList(rule.instructions)
  }
}

function normalizeDocumentRule(rule) {
  var normalized = normalizeRule(rule)
  if (!normalized) return null
  normalized.documentUrls = normalizeStringList(rule.documentUrls)
  return normalized
}

function normalizePolicyDocument(document) {
  if (!document || typeof document !== 'object') return null
  var label = String(document.label || '').trim()
  var url = String(document.url || '').trim()
  if (!label && !url) return null
  return {
    label: label || url,
    url: url,
    language: String(document.language || '').trim(),
    usageInstruction: String(document.usageInstruction || '').trim(),
    ruleId: String(document.ruleId || '').trim(),
    ruleLabel: String(document.ruleLabel || '').trim()
  }
}

function renderEmailPreviewUi(emailPreviewUi, translations) {
  var labels = normalizeEmailPreviewTranslations(translations, emailPreviewUi)
  var activeIndex = emailPreviewUi.activeEmailId
    ? Math.max(0, emailPreviewUi.emails.findIndex(function (email) { return email.id === emailPreviewUi.activeEmailId }))
    : 0
  var activeEmail = emailPreviewUi.emails[activeIndex] || emailPreviewUi.emails[0] || null

  return (
    '<div class="wc-email-preview-ui" data-email-preview-page-size="' + escapeAttributeValue(String(emailPreviewUi.pageSize || 10)) + '">' +
      '<div class="wc-ds-capability wc-ds-capability-plume">' +
        '<div class="wc-ds-capability-name">' + renderLucideIcon('feather') + '<span>@Plume</span></div>' +
        '<div class="wc-ds-capability-meta">' + escapeHtml(emailPreviewUi.title || labels.title) + '</div>' +
      '</div>' +
      '<div class="wc-email-preview-head">' +
        '<div class="wc-email-preview-head-copy">' +
          '<div class="wc-email-preview-title">' + escapeHtml(emailPreviewUi.title || labels.title) + '</div>' +
          renderTargetLanguageBadge(emailPreviewUi, labels) +
          '<div class="wc-email-preview-summary">' +
            '<span class="wc-email-preview-summary-count" data-email-preview-count="' + escapeAttributeValue(String(emailPreviewUi.validatedCount || 0)) + '">' + escapeHtml(String(emailPreviewUi.validatedCount || 0)) + '</span>' +
            '<span>' + escapeHtml(emailPreviewUi.validatedCountLabel || labels.validatedCountLabel) + '</span>' +
            '<span class="wc-email-preview-summary-total">' + escapeHtml(' / ' + String(emailPreviewUi.totalCount || 0)) + '</span>' +
          '</div>' +
        '</div>' +
        '<div class="wc-email-preview-actions">' +
          renderCampaignTestAction(emailPreviewUi.testAction) +
          '<button type="button" class="wc-email-preview-send" ' + ((emailPreviewUi.validatedCount || 0) ? '' : 'disabled') + ' data-email-preview-send-template="' + escapeAttributeValue(emailPreviewUi.sendButtonTemplate || labels.sendButtonTemplate) + '">' +
            renderLucideIcon('send') + '<span>' + escapeHtml(renderSendLabel(emailPreviewUi.sendButtonTemplate || labels.sendButtonTemplate, emailPreviewUi.validatedCount || 0)) + '</span>' +
          '</button>' +
        '</div>' +
      '</div>' +
      '<div class="wc-email-preview-body">' +
        '<aside class="wc-email-preview-list">' +
          '<div class="wc-email-preview-list-scroll">' +
            renderEmailPreviewList(emailPreviewUi, labels, activeIndex) +
          '</div>' +
          '<div class="wc-email-preview-list-foot">' +
            '<button type="button" class="wc-email-preview-page wc-email-preview-page-prev">' + renderLucideIcon('chevronLeft') + '<span>' + escapeHtml(emailPreviewUi.previousLabel || labels.previousLabel) + '</span></button>' +
            '<span class="wc-email-preview-page-label" data-email-preview-page-label="1">' + escapeHtml(renderPageLabel(emailPreviewUi.pageLabel || labels.pageLabel, Math.floor(activeIndex / (emailPreviewUi.pageSize || 10)) + 1, getPageCount(emailPreviewUi))) + '</span>' +
            '<button type="button" class="wc-email-preview-page wc-email-preview-page-next"><span>' + escapeHtml(emailPreviewUi.nextLabel || labels.nextLabel) + '</span>' + renderLucideIcon('chevronRight') + '</button>' +
          '</div>' +
        '</aside>' +
        '<section class="wc-email-preview-panel" aria-live="polite">' +
          renderEmailPreviewPanel(activeEmail, emailPreviewUi, labels) +
        '</section>' +
      '</div>' +
    '</div>'
  )
}

function renderCampaignTestAction(action) {
  if (!action) return ''
  return (
    '<button type="button" class="wc-email-preview-test-action" data-email-campaign-test-action="true" data-batch-id="' + escapeAttributeValue(action.batchId || '') + '"' + (action.enabled ? '' : ' disabled') + '>' +
      renderLucideIcon('mailCheck') +
      '<span>' + escapeHtml(action.label || 'Envoyer un test') + '</span>' +
    '</button>'
  )
}

function renderTargetLanguageBadge(emailPreviewUi, labels) {
  if (!emailPreviewUi.targetLanguageDisplay) {
    return ''
  }

  return '<div class="wc-email-preview-language" data-email-preview-language>' +
    '<span class="wc-email-preview-language-label">' + escapeHtml(emailPreviewUi.targetLanguageLabel || labels.targetLanguageLabel) + '</span>' +
    '<span class="wc-email-preview-language-value">' + escapeHtml(emailPreviewUi.targetLanguageDisplay) + '</span>' +
  '</div>'
}

function renderEmailPreviewList(emailPreviewUi, labels, activeIndex) {
  if (!emailPreviewUi.emails.length) {
    return '<div class="wc-email-preview-empty">Aucun email disponible.</div>'
  }

  var pageSize = emailPreviewUi.pageSize || 10
  var activePage = Math.floor(activeIndex / pageSize) + 1
  return emailPreviewUi.emails.map(function (email, index) {
    var page = Math.floor(index / pageSize) + 1
    var visible = page === activePage ? '' : ' hidden'
    var active = index === activeIndex ? ' active' : ''
    var done = email.validated ? ' done' : ''
    var role = email.recipient.jobTitle ? '<span class="wc-email-preview-list-role">' + escapeHtml(email.recipient.jobTitle) + '</span>' : ''
    var company = email.recipient.company ? '<div class="wc-email-preview-list-company">' + escapeHtml(email.recipient.company) + '</div>' : ''
    var sourceObject = renderSourceObjectLine(email.recipient.sourceObject, 'wc-email-preview-list-company')
    var statusChip = email.recipient.status === 'no_optin'
      ? '<span class="wc-email-preview-list-chip warning">' + escapeHtml(email.recipient.statusLabel || 'Non optin') + '</span>'
      : ''

    return (
      '<div class="wc-email-preview-item' + active + done + '" data-email-preview-index="' + index + '"' + visible + '>' +
        '<input type="checkbox" class="wc-email-preview-check" data-email-preview-checkbox aria-label="' + escapeAttributeValue((email.validated ? labels.validatedLabel : labels.validateLabel) + ' ' + (email.recipient.name || ('Email ' + (index + 1)))) + '"' + (email.validated ? ' checked' : '') + '>' +
        '<button type="button" class="wc-email-preview-item-select" data-email-preview-select>' +
          '<div class="wc-email-preview-list-title"><strong class="wc-email-preview-list-name">' + escapeHtml(email.recipient.name || ('Email ' + (index + 1))) + '</strong>' + statusChip + '</div>' +
          role +
          company +
          sourceObject +
        '</button>' +
      '</div>'
    )
  }).join('')
}

function renderEmailPreviewPanel(email, emailPreviewUi, labels) {
  if (!email) {
    return '<div class="wc-email-preview-empty-panel">Aucun email sélectionné.</div>'
  }

  var subjectId = 'wc-email-preview-subject'
  var bodyId = 'wc-email-preview-body'

  return (
    '<div class="wc-email-preview-panel-inner" data-email-preview-panel>' +
      '<div class="wc-email-preview-panel-head">' +
        '<div class="wc-email-preview-panel-head-copy">' +
          '<div class="wc-email-preview-contact-name" data-email-preview-contact-name>' + escapeHtml(email.recipient.name || labels.recipientLabel) + '</div>' +
          '<div class="wc-email-preview-contact-meta" data-email-preview-contact-meta>' + escapeHtml(renderContactMeta(email)) + '</div>' +
        '</div>' +
        '<button type="button" class="wc-email-preview-validate" data-email-preview-validate>' + renderLucideIcon('check') + '<span>' + escapeHtml(email.validated ? emailPreviewUi.validatedLabel : (emailPreviewUi.validateLabel || labels.validateLabel)) + '</span></button>' +
      '</div>' +
      '<div class="wc-email-preview-recipient">' +
        '<div class="wc-email-preview-recipient-row">' +
          '<span class="wc-email-preview-recipient-key">' + escapeHtml(emailPreviewUi.recipientLabel || labels.recipientLabel) + '</span>' +
          '<span class="wc-email-preview-recipient-value" data-email-preview-recipient-email>' + escapeHtml(email.recipient.email || '') + '</span>' +
        '</div>' +
        renderSourceObjectRecipientRow(email.recipient.sourceObject) +
      '</div>' +
      '<div class="wc-email-preview-fields">' +
        '<label class="wc-email-preview-field" for="' + subjectId + '">' +
          '<span>' + escapeHtml(emailPreviewUi.subjectLabel || labels.subjectLabel) + '</span>' +
          '<input id="' + subjectId + '" type="text" class="wc-email-preview-input" data-email-preview-subject value="' + escapeAttributeValue(email.subject || '') + '">' +
        '</label>' +
      '</div>' +
      '<div class="wc-email-preview-bodywrap">' +
        '<div class="wc-email-preview-field wc-email-preview-field-body">' +
          '<span>' + escapeHtml(emailPreviewUi.bodyLabel || labels.bodyLabel) + '</span>' +
          '<div id="' + bodyId + '" class="wc-email-preview-body-editor" data-email-preview-body contenteditable="true" role="textbox" aria-multiline="true" aria-label="' + escapeAttributeValue(emailPreviewUi.bodyLabel || labels.bodyLabel) + '">' + sanitizeEmailBodyHtml(email.body || '') + '</div>' +
        '</div>' +
      '</div>' +
      '<div class="wc-email-preview-policy-slot" data-email-preview-policy>' + renderWritingPolicyPanel(email.sendingProfile) + '</div>' +
      '<div class="wc-email-preview-instruction-slot" data-email-preview-instruction>' + renderCustomPromptReport(email.customPrompt, emailPreviewUi) + '</div>' +
      '<div class="wc-email-preview-nav">' +
        '<button type="button" class="wc-email-preview-navbtn wc-email-preview-nav-prev">' + renderLucideIcon('chevronLeft') + '<span>' + escapeHtml(emailPreviewUi.previousLabel || labels.previousLabel) + '</span></button>' +
        '<span class="wc-email-preview-counter" data-email-preview-counter>1 / 1</span>' +
        '<button type="button" class="wc-email-preview-navbtn wc-email-preview-nav-next"><span>' + escapeHtml(emailPreviewUi.nextLabel || labels.nextLabel) + '</span>' + renderLucideIcon('chevronRight') + '</button>' +
      '</div>' +
    '</div>'
  )
}

function renderCustomPromptReport(customPrompt, labels) {
  if (!customPrompt || !customPrompt.report) return ''

  var report = customPrompt.report
  var levelClass = report.status === 'applied' ? '' : ' warning'
  var applied = report.applied.length
    ? '<section class="wc-email-preview-instruction-group"><strong>' + escapeHtml(labels.customPromptAppliedLabel || 'Applications') + '</strong><ul>' + report.applied.map(function (item) { return '<li>' + escapeHtml(item) + '</li>' }).join('') + '</ul></section>'
    : ''
  var limitations = report.limitations.length
    ? '<section class="wc-email-preview-instruction-group"><strong>' + escapeHtml(labels.customPromptLimitationsLabel || 'Limitations') + '</strong><ul>' + report.limitations.map(function (item) { return '<li>' + escapeHtml(item) + '</li>' }).join('') + '</ul></section>'
    : ''

  if (!applied && !limitations) return ''

  return (
    '<details class="wc-email-preview-instruction' + levelClass + '">' +
      '<summary class="wc-email-preview-instruction-summary">' +
        renderLucideIcon(report.status === 'applied' ? 'badgeInfo' : 'triangleAlert') +
        '<span>' + escapeHtml(labels.customPromptReportLabel || 'Choix de rédaction effectués') + '</span>' +
      '</summary>' +
      '<div class="wc-email-preview-instruction-content">' + applied + limitations + '</div>' +
    '</details>'
  )
}

function renderWritingPolicyPanel(policy) {
  if (!policy) return ''

  return (
    '<details class="wc-email-preview-policy">' +
      '<summary class="wc-email-preview-policy-summary">' +
        renderLucideIcon('badgeInfo') +
        '<span>Politique appliquée</span>' +
        '<strong>' + escapeHtml(policy.policyName) + '</strong>' +
        '<em>v' + escapeHtml(String(policy.policyVersion || 1)) + '</em>' +
      '</summary>' +
      '<div class="wc-email-preview-policy-content">' +
        renderPolicyRuleGroup('Langue', policy.appliedLanguageRules, renderLanguageRuleMeta) +
        renderPolicyRuleGroup('Contenu', policy.appliedContentRules, renderInstructionMeta) +
        renderPolicyRuleGroup('Documents', policy.appliedDocumentRules, renderDocumentRuleMeta) +
        renderSelectedDocuments(policy.selectedDocuments) +
        renderDataFields(policy.dataFields) +
      '</div>' +
    '</details>'
  )
}

function renderPolicyRuleGroup(title, rules, renderMeta) {
  if (!Array.isArray(rules) || !rules.length) return ''

  return (
    '<section class="wc-email-preview-policy-group">' +
      '<div class="wc-email-preview-policy-title">' + escapeHtml(title) + '</div>' +
      '<div class="wc-email-preview-policy-chips">' +
        rules.map(function (rule) {
          var meta = typeof renderMeta === 'function' ? renderMeta(rule) : ''
          return '<span class="wc-email-preview-policy-chip" title="' + escapeAttributeValue(rule.id || '') + '">' +
            escapeHtml(rule.label || rule.id || 'Règle') +
            (meta ? '<small>' + escapeHtml(meta) + '</small>' : '') +
          '</span>'
        }).join('') +
      '</div>' +
    '</section>'
  )
}

function renderSelectedDocuments(documents) {
  if (!Array.isArray(documents) || !documents.length) return ''

  return (
    '<section class="wc-email-preview-policy-group">' +
      '<div class="wc-email-preview-policy-title">Supports retenus</div>' +
      '<div class="wc-email-preview-policy-docs">' +
        documents.map(function (document) {
          var meta = [document.language ? document.language.toUpperCase() : '', document.ruleLabel].filter(Boolean).join(' · ')
          return '<a class="wc-email-preview-policy-doc" href="' + escapeAttributeValue(document.url || '#') + '" target="_blank" rel="noopener noreferrer">' +
            renderLucideIcon('fileText') +
            '<span>' +
              '<strong>' + escapeHtml(document.label || document.url || 'Document') + '</strong>' +
              (meta ? '<small>' + escapeHtml(meta) + '</small>' : '') +
            '</span>' +
          '</a>'
        }).join('') +
      '</div>' +
    '</section>'
  )
}

function renderDataFields(fields) {
  if (!Array.isArray(fields) || !fields.length) return ''

  return (
    '<section class="wc-email-preview-policy-group">' +
      '<div class="wc-email-preview-policy-title">Champs utilisés</div>' +
      '<div class="wc-email-preview-policy-chips">' +
        fields.map(function (field) {
          var label = field.label || field.path
          return '<span class="wc-email-preview-policy-chip muted">' +
            escapeHtml(label) +
            '<small>' + escapeHtml(field.scope + '.' + field.path + (field.required ? ' · requis' : '')) + '</small>' +
          '</span>'
        }).join('') +
      '</div>' +
    '</section>'
  )
}

function renderLanguageRuleMeta(rule) {
  return [rule.language ? rule.language.toUpperCase() : '', rule.politeness].filter(Boolean).join(' · ')
}

function renderInstructionMeta(rule) {
  return rule.instructions && rule.instructions.length ? String(rule.instructions.length) + ' instruction(s)' : ''
}

function renderDocumentRuleMeta(rule) {
  return rule.documentUrls && rule.documentUrls.length ? String(rule.documentUrls.length) + ' support(s)' : ''
}

function bindEmailPreviewUi(el, emailPreviewUi, handlers) {
  if (!el || !emailPreviewUi) return

  var state = {
    emails: emailPreviewUi.emails.map(function (email) {
      return Object.assign({}, email)
    }),
    activeIndex: emailPreviewUi.activeEmailId
      ? Math.max(0, emailPreviewUi.emails.findIndex(function (email) { return email.id === emailPreviewUi.activeEmailId }))
      : 0,
    pageSize: emailPreviewUi.pageSize || 10
  }

  var root = el.querySelector('.wc-email-preview-ui')
  var panel = el.querySelector('[data-email-preview-panel]')
  var listScroll = el.querySelector('.wc-email-preview-list-scroll')
  var sendButton = el.querySelector('.wc-email-preview-send')
  var testButton = el.querySelector('.wc-email-preview-test-action')
  var countBadge = el.querySelector('[data-email-preview-count]')
  var pageLabel = el.querySelector('[data-email-preview-page-label]')
  var pagePrev = el.querySelector('.wc-email-preview-page-prev')
  var pageNext = el.querySelector('.wc-email-preview-page-next')
  var panelName = el.querySelector('[data-email-preview-contact-name]')
  var panelMeta = el.querySelector('[data-email-preview-contact-meta]')
  var validateButton = el.querySelector('[data-email-preview-validate]')
  var recipientEmail = el.querySelector('[data-email-preview-recipient-email]')
  var sourceObjectRow = el.querySelector('[data-email-preview-source-row]')
  var sourceObjectKey = el.querySelector('[data-email-preview-source-key]')
  var sourceObjectValue = el.querySelector('[data-email-preview-source-value]')
  var subjectInput = el.querySelector('[data-email-preview-subject]')
  var bodyInput = el.querySelector('[data-email-preview-body]')
  var counter = el.querySelector('[data-email-preview-counter]')
  var policySlot = el.querySelector('[data-email-preview-policy]')
  var instructionSlot = el.querySelector('[data-email-preview-instruction]')
  var draftTimer = null

  function getPageCount() {
    return Math.max(1, Math.ceil(state.emails.length / state.pageSize))
  }

  function getActiveEmail() {
    return state.emails[state.activeIndex] || null
  }

  function renderActiveEmail() {
    var email = getActiveEmail()
    if (!email) return

    panelName.textContent = email.recipient.name || ''
    panelMeta.textContent = renderContactMeta(email)
    validateButton.innerHTML = renderLucideIcon('check') + '<span>' + escapeHtml(email.validated ? emailPreviewUi.validatedLabel : emailPreviewUi.validateLabel) + '</span>'
    recipientEmail.textContent = email.recipient.email || ''
    renderActiveSourceObject(email.recipient.sourceObject)
    subjectInput.value = email.subject || ''
    setBodyEditorValue(bodyInput, email.body || '')
    if (policySlot) {
      policySlot.innerHTML = renderWritingPolicyPanel(email.sendingProfile)
    }
    if (instructionSlot) {
      instructionSlot.innerHTML = renderCustomPromptReport(email.customPrompt, emailPreviewUi)
    }
    counter.textContent = renderCounter(state.activeIndex + 1, state.emails.length)
    root.classList.toggle('wc-email-preview-validated', email.validated)
  }

  function renderActiveSourceObject(sourceObject) {
    if (!sourceObjectRow || !sourceObjectKey || !sourceObjectValue) return
    var hasSourceObject = Boolean(sourceObject && sourceObject.label)
    sourceObjectRow.hidden = !hasSourceObject
    sourceObjectKey.textContent = hasSourceObject ? (sourceObject.typeLabel || 'Objet') : ''
    sourceObjectValue.textContent = hasSourceObject ? sourceObject.label : ''
  }

  function renderList() {
    var currentPage = Math.floor(state.activeIndex / state.pageSize) + 1
    var pageCount = getPageCount()
    if (pageLabel) {
      pageLabel.textContent = renderPageLabel(emailPreviewUi.pageLabel, currentPage, pageCount)
    }

    Array.prototype.slice.call(listScroll.querySelectorAll('.wc-email-preview-item')).forEach(function (item) {
      var index = Number(item.getAttribute('data-email-preview-index'))
      var email = state.emails[index]
      var page = Math.floor(index / state.pageSize) + 1
      item.hidden = page !== currentPage
      item.classList.toggle('active', index === state.activeIndex)
      item.classList.toggle('done', Boolean(email && email.validated))

      var check = item.querySelector('[data-email-preview-checkbox]')
      if (check) {
        check.checked = Boolean(email && email.validated)
        check.setAttribute('aria-label', (email && email.validated ? emailPreviewUi.validatedLabel : emailPreviewUi.validateLabel) + ' ' + (email && email.recipient ? email.recipient.name : 'email'))
      }
    })

    if (pagePrev) {
      pagePrev.disabled = currentPage <= 1
    }
    if (pageNext) {
      pageNext.disabled = currentPage >= pageCount
    }
  }

  function syncTopBar() {
    var validatedCount = state.emails.filter(function (email) { return email.validated }).length
    if (countBadge) countBadge.textContent = String(validatedCount)
    if (sendButton) {
      sendButton.innerHTML = renderLucideIcon('send') + '<span>' + escapeHtml(renderSendLabel(emailPreviewUi.sendButtonTemplate, validatedCount)) + '</span>'
      sendButton.disabled = validatedCount <= 0
    }
  }

  function setActiveIndex(nextIndex) {
    if (!state.emails.length) return
    state.activeIndex = clamp(nextIndex, 0, state.emails.length - 1)
    renderActiveEmail()
    renderList()
    syncTopBar()
  }

  function movePage(delta) {
    var currentPage = Math.floor(state.activeIndex / state.pageSize) + 1
    var nextPage = clamp(currentPage + delta, 1, getPageCount())
    setActiveIndex((nextPage - 1) * state.pageSize)
  }

  function updateCurrentEmail() {
    var email = getActiveEmail()
    if (!email) return
    email.subject = String(subjectInput.value || '').trim()
    email.body = getBodyEditorValue(bodyInput)
  }

  function persistCurrentEmailDraft() {
    var email = getActiveEmail()
    if (!email || !handlers || typeof handlers.onDraftChange !== 'function') return

    var payload = {
      batchId: emailPreviewUi.batchId || null,
      emailId: email.id,
      activeEmailId: email.id,
      subject: email.subject,
      body: email.body,
      recipient: email.recipient
    }

    if (email.validated) {
      payload.validated = true
    }

    handlers.onDraftChange(payload)
  }

  function scheduleDraftPersistence() {
    if (!handlers || typeof handlers.onDraftChange !== 'function') return
    if (draftTimer) {
      clearTimeout(draftTimer)
    }
    draftTimer = setTimeout(function () {
      draftTimer = null
      persistCurrentEmailDraft()
    }, 250)
  }

  function persistValidation(email) {
    if (!email || !handlers || typeof handlers.onValidate !== 'function') return
    handlers.onValidate({
      batchId: emailPreviewUi.batchId || null,
      emailId: email.id,
      activeEmailId: email.id,
      validated: email.validated,
      subject: email.subject,
      body: email.body,
      recipient: email.recipient
    })
  }

  function setEmailValidation(index, validated) {
    var email = state.emails[index]
    if (!email) return
    var previousIndex = state.activeIndex
    if (draftTimer) {
      clearTimeout(draftTimer)
      draftTimer = null
    }
    updateCurrentEmail()
    if (index !== previousIndex) {
      persistCurrentEmailDraft()
    }
    email.validated = validated
    setActiveIndex(index)
    persistValidation(email)
  }

  function validateCurrentEmail() {
    var email = getActiveEmail()
    if (!email) return
    if (draftTimer) {
      clearTimeout(draftTimer)
      draftTimer = null
    }
    updateCurrentEmail()
    email.validated = true
    renderActiveEmail()
    renderList()
    syncTopBar()
    persistValidation(email)
  }

  function emitSend() {
    updateCurrentEmail()
    var validatedEmails = state.emails.filter(function (email) { return email.validated })
    if (handlers && typeof handlers.onSend === 'function') {
      handlers.onSend({
        label: renderSendLabel(emailPreviewUi.sendButtonTemplate, validatedEmails.length),
        nextStepKey: emailPreviewUi.nextStep && emailPreviewUi.nextStep.key ? emailPreviewUi.nextStep.key : 'EMailSend',
        batchId: emailPreviewUi.batchId || null,
        validatedCount: validatedEmails.length,
        totalCount: state.emails.length,
        emails: state.emails,
        validatedEmails: validatedEmails,
        activeEmailId: getActiveEmail() ? getActiveEmail().id : null
      })
    }
  }

  Array.prototype.slice.call(el.querySelectorAll('.wc-email-preview-item')).forEach(function (item) {
    var select = item.querySelector('[data-email-preview-select]')
    var checkbox = item.querySelector('[data-email-preview-checkbox]')
    if (select) {
      select.addEventListener('click', function () {
        setActiveIndex(Number(item.getAttribute('data-email-preview-index')))
      })
    }
    if (checkbox) {
      checkbox.addEventListener('change', function () {
        setEmailValidation(Number(item.getAttribute('data-email-preview-index')), checkbox.checked === true)
      })
    }
  })

  if (subjectInput) {
    subjectInput.addEventListener('input', function () {
      updateCurrentEmail()
      scheduleDraftPersistence()
    })
    subjectInput.addEventListener('blur', persistCurrentEmailDraft)
  }
  if (bodyInput) {
    bodyInput.addEventListener('input', function () {
      updateCurrentEmail()
      scheduleDraftPersistence()
    })
    bodyInput.addEventListener('blur', persistCurrentEmailDraft)
  }
  if (validateButton) {
    validateButton.addEventListener('click', validateCurrentEmail)
  }
  if (pagePrev) {
    pagePrev.addEventListener('click', function () {
      movePage(-1)
    })
  }
  if (pageNext) {
    pageNext.addEventListener('click', function () {
      movePage(1)
    })
  }
  if (sendButton) {
    sendButton.addEventListener('click', emitSend)
  }
  if (testButton && emailPreviewUi.testAction) {
    testButton.addEventListener('click', function () {
      if (handlers && typeof handlers.onCampaignTest === 'function') {
        handlers.onCampaignTest(emailPreviewUi.testAction)
      }
    })
  }
  if (el.querySelector('.wc-email-preview-nav-prev')) {
    el.querySelector('.wc-email-preview-nav-prev').addEventListener('click', function () {
      setActiveIndex(state.activeIndex - 1)
    })
  }
  if (el.querySelector('.wc-email-preview-nav-next')) {
    el.querySelector('.wc-email-preview-nav-next').addEventListener('click', function () {
      setActiveIndex(state.activeIndex + 1)
    })
  }

  var initialIndex = state.activeIndex
  if (!emailPreviewUi.activeEmailId) {
    var firstValidatedIndex = emailPreviewUi.emails.findIndex(function (email) { return email.validated })
    initialIndex = firstValidatedIndex >= 0 ? firstValidatedIndex : 0
  }

  setActiveIndex(initialIndex)
  syncTopBar()
}

function renderSendLabel(template, count) {
  return String(template || 'Envoyer les {{count}} emails validés').replace(/\{\{\s*count\s*\}\}/g, String(count))
}

function renderPageLabel(label, page, pageCount) {
  return String(label || 'Page') + ' ' + page + '/' + pageCount
}

function renderCounter(position, total) {
  return String(position) + ' / ' + String(total)
}

function renderContactMeta(email) {
  var source = formatSourceObject(email.recipient.sourceObject)
  return [email.recipient.jobTitle, email.recipient.company, source].filter(Boolean).join(' · ')
}

function renderSourceObjectRecipientRow(sourceObject) {
  var hidden = sourceObject && sourceObject.label ? '' : ' hidden'
  return (
    '<div class="wc-email-preview-recipient-row" data-email-preview-source-row' + hidden + '>' +
      '<span class="wc-email-preview-recipient-key" data-email-preview-source-key>' + escapeHtml(sourceObject && sourceObject.typeLabel ? sourceObject.typeLabel : 'Objet') + '</span>' +
      '<span class="wc-email-preview-recipient-value" data-email-preview-source-value>' + escapeHtml(sourceObject && sourceObject.label ? sourceObject.label : '') + '</span>' +
    '</div>'
  )
}

function renderSourceObjectLine(sourceObject, className) {
  var formatted = formatSourceObject(sourceObject)
  return formatted ? '<div class="' + className + '">' + escapeHtml(formatted) + '</div>' : ''
}

function formatSourceObject(sourceObject) {
  if (!sourceObject || !sourceObject.label) return ''
  return [sourceObject.typeLabel, sourceObject.label].filter(Boolean).join(' · ')
}

function normalizeEmailPreviewTranslations(translations, ui) {
  return Object.assign({
    title: 'Prévisualisation des emails',
    validatedCountLabel: 'Emails validés',
    sendButtonTemplate: 'Envoyer les {{count}} emails validés',
    recipientLabel: 'Destinataire',
    subjectLabel: 'Objet',
    bodyLabel: "Corps de l'email",
    targetLanguageLabel: 'Langue cible',
    previousLabel: 'Précédent',
    nextLabel: 'Suivant',
    validateLabel: 'Valider',
    validatedLabel: 'Validé',
    pageLabel: 'Page'
  }, translations && translations.preview ? translations.preview : {}, ui || {})
}

function normalizeNextStep(nextStep) {
  if (!nextStep || typeof nextStep !== 'object') {
    return { key: 'EMailSend', label: 'EMailSend' }
  }

  return {
    key: String(nextStep.key || 'EMailSend'),
    label: String(nextStep.label || nextStep.key || 'EMailSend')
  }
}

function normalizeCount(value) {
  var numeric = Number(value)
  return Number.isFinite(numeric) && numeric >= 0 ? Math.floor(numeric) : 0
}

function normalizeStringList(value) {
  if (!Array.isArray(value)) return []
  return value.map(function (item) {
    return String(item || '').trim()
  }).filter(Boolean)
}

function clamp(value, min, max) {
  return Math.max(min, Math.min(max, value))
}

function getPageCount(emailPreviewUi) {
  return Math.max(1, Math.ceil((emailPreviewUi.emails.length || 0) / (emailPreviewUi.pageSize || 10)))
}

function sanitizeEmailBodyHtml(value) {
  return String(value || '')
    .replace(/<script[\s\S]*?>[\s\S]*?<\/script>/gi, '')
    .replace(/<iframe[\s\S]*?>[\s\S]*?<\/iframe>/gi, '')
    .replace(/<object[\s\S]*?>[\s\S]*?<\/object>/gi, '')
    .replace(/<embed[\s\S]*?>[\s\S]*?<\/embed>/gi, '')
    .replace(/\son[a-z]+\s*=\s*("[^"]*"|'[^']*'|[^\s>]+)/gi, '')
    .replace(/\s(href|src)\s*=\s*("[^"]*"|'[^']*'|[^\s>]+)/gi, function (_match, attribute, rawValue) {
      var unquoted = String(rawValue || '').replace(/^['"]|['"]$/g, '').trim()
      return isAllowedEmailBodyUrl(unquoted)
        ? ' ' + attribute + '="' + escapeAttributeValue(unquoted) + '"'
        : ''
    })
    .replace(/\r\n?|\n/g, '<br>')
}

function isAllowedEmailBodyUrl(value) {
  return /^(https?:|mailto:)/i.test(value) || /^data:image\/[a-z0-9.+-]+;base64,[a-z0-9+/=\s]+$/i.test(value)
}

function setBodyEditorValue(element, value) {
  if (!element) return
  if (typeof element.innerHTML === 'string') {
    element.innerHTML = sanitizeEmailBodyHtml(value)
    return
  }
  element.value = String(value || '')
}

function getBodyEditorValue(element) {
  if (!element) return ''
  if (typeof element.innerHTML === 'string') return sanitizeEmailBodyHtml(element.innerHTML)
  return String(element.value || '')
}

function escapeHtml(value) {
  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
}

function escapeAttributeValue(value) {
  return escapeHtml(value).replace(/`/g, '&#96;')
}

export { normalizeEmailPreviewUi, renderEmailPreviewUi, bindEmailPreviewUi }
